import { FC, useEffect, useState } from 'react'

import styled, { useTheme } from 'styled-components'
import { Progress } from 'antd'

import CloseCross from 'src/Assets/Svg/close-cross.svg?react'
import { PrimaryDottedButton } from 'src/Components/Buttons/PrimaryDottedButton'
import { MAX_MOBILE_WIDTH, variables } from 'src/Styled/variables'

export {
  DiscountTotalsLine,
  type DiscountTotalsLineProps,
} from './DiscountTotalsLine'

export type ThresholdCouponBannerState =
  | 'offered'
  | 'attachedBelowThreshold'
  | 'unlocked'

const FULL_PERCENT = 100

const Container = styled.div<{ $state: ThresholdCouponBannerState }>`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 24px;
  border-radius: 16px;
  color: ${({ theme }) => theme.color.darkGrey};
  font-family: 'Segoe UI';
  font-size: ${variables.fontSize14};
  font-style: normal;
  font-weight: ${variables.fontWeight400};
  line-height: 150%;
  background-color: ${({ theme, $state }) =>
    $state === 'attachedBelowThreshold'
      ? theme.color.hoverWhite
      : theme.color.lightGreen};

  @media screen and (max-width: ${MAX_MOBILE_WIDTH}) {
    padding: 12px 16px;
    font-size: ${variables.fontSize12};
    width: 100%;
  }
`

const Row = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`

const Grow = styled.div`
  flex: 1;
`

const Divider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.color.lightGrey};
`

const CouponDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const CouponName = styled.strong`
  color: ${({ theme }) => theme.color.darkGreen};
  font-weight: ${variables.fontWeight600};
  margin-right: 8px;
`

const CouponStatus = styled.span<{ $isApplied: boolean }>`
  color: ${({ theme, $isApplied }) =>
    $isApplied ? theme.color.darkGreen : theme.color.slateGrey};
  font-weight: ${({ $isApplied }) =>
    $isApplied ? variables.fontWeight600 : variables.fontWeight400};
`

const GreenText = styled.div`
  color: ${({ theme }) => theme.color.darkGreen};
`

const TagIcon = styled.span`
  flex-shrink: 0;
`

const ApplyAction = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 8px;
`

const CloseButton = styled.button.attrs({ type: 'button' })`
  flex-shrink: 0;
  display: flex;
  background: none;
  border: none;
  padding: 2px;
  cursor: pointer;
  color: ${({ theme }) => theme.color.slateGrey};

  &:focus-visible {
    outline: 1px solid ${({ theme }) => theme.color.darkGreen};
    outline-offset: 2px;
  }
`

export interface ThresholdCouponBannerProps {
  state: ThresholdCouponBannerState
  code: string
  percent: number | null
  threshold: string
  raisePayable?: string
  remaining: string
  saved: string
  progress: number
  onApply: () => void
  onDismiss: () => void
  onRemove: () => void
  onRaiseToThreshold?: () => void
}

const getRaiseLabel = (
  code: string,
  threshold: string,
  raisePayable?: string,
): string => {
  const label = `Apply ${code} and raise the campaign total to ${threshold}`
  return raisePayable ? `${label}, ${raisePayable} after discount` : label
}

const normalizeProgress = (value: number): number => {
  if (!Number.isFinite(value)) {
    return 0
  }
  return Math.round(Math.min(FULL_PERCENT, Math.max(0, value)))
}

const getDiscountLabel = (percent: number | null, fallback: string): string =>
  percent === null ? fallback : `${percent}% off`

export const ThresholdCouponBanner: FC<ThresholdCouponBannerProps> = ({
  state,
  code,
  percent,
  threshold,
  raisePayable,
  remaining,
  saved,
  progress,
  onApply,
  onDismiss,
  onRemove,
  onRaiseToThreshold,
}) => {
  const theme = useTheme()
  const [animatedPercent, setAnimatedPercent] = useState(0)
  const targetPercent = normalizeProgress(progress)

  useEffect(() => {
    const nextPercent = state === 'offered' ? 0 : targetPercent
    const frame = requestAnimationFrame(() => setAnimatedPercent(nextPercent))
    return () => cancelAnimationFrame(frame)
  }, [state, targetPercent])

  if (state === 'offered') {
    return (
      <Container $state={state}>
        <Row>
          <TagIcon aria-hidden>🏷️</TagIcon>
          <Grow>
            Order over <strong>{threshold}</strong> and receive{' '}
            <strong>{getDiscountLabel(percent, 'a discount')}</strong> with code{' '}
            {code}
            <ApplyAction>
              <PrimaryDottedButton onClick={onApply}>Apply</PrimaryDottedButton>
            </ApplyAction>
          </Grow>
          <CloseButton
            aria-label={`Dismiss the ${code} offer`}
            onClick={onDismiss}
          >
            <CloseCross />
          </CloseButton>
        </Row>
      </Container>
    )
  }

  const isUnlocked = state === 'unlocked'
  const remainingCopy = (
    <>
      Add <strong>{remaining}</strong> to unlock{' '}
      <strong>{getDiscountLabel(percent, 'your discount')}</strong> with {code}.
    </>
  )

  return (
    <Container $state={state}>
      <div aria-hidden>
        <Progress
          percent={animatedPercent}
          showInfo={false}
          trailColor={theme.color.lightGrey}
          strokeColor={theme.color.brandGreen}
          size={{ height: 6 }}
        />
      </div>
      <div role='status' aria-live='polite'>
        {isUnlocked ? (
          <GreenText>
            <strong>{getDiscountLabel(percent, 'Discount')} unlocked.</strong>{' '}
            You saved {saved} with {code}.
          </GreenText>
        ) : (
          remainingCopy
        )}
      </div>
      <Divider />
      <CouponDetails>
        <Row>
          <TagIcon aria-hidden>🏷️</TagIcon>
          <Grow>
            <CouponName>{code}</CouponName>
            <CouponStatus $isApplied={isUnlocked}>
              {isUnlocked ? 'applied' : 'not applied yet'}
            </CouponStatus>
          </Grow>
          <CloseButton
            aria-label={`Remove the ${code} coupon`}
            onClick={onRemove}
          >
            <CloseCross />
          </CloseButton>
        </Row>
        <GreenText>
          {isUnlocked ? (
            <>
              {getDiscountLabel(percent, 'Discount on')} orders over {threshold}
              . You saved {saved} on this campaign.
            </>
          ) : (
            <>
              {remainingCopy}
              {onRaiseToThreshold && (
                <ApplyAction>
                  <PrimaryDottedButton
                    onClick={onRaiseToThreshold}
                    aria-label={getRaiseLabel(code, threshold, raisePayable)}
                  >
                    Apply
                  </PrimaryDottedButton>
                </ApplyAction>
              )}
            </>
          )}
        </GreenText>
      </CouponDetails>
    </Container>
  )
}
